import * as React from 'react'
import { ReactElement, useState } from 'react'
import BaseList from '../../../components/List/BaseList'
import { useFormikContext } from 'formik'
import { RateDto } from '../../../config/fileConfig'
import { Button } from '@fluentui/react-components'
import { GuestAdd24Regular } from '@fluentui/react-icons'
import TierDialog, { TierDialogProps } from './TierDialog'
import { BaseTierDto } from '../../../config/finance/financeConfig'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import useUsers, { UsersProps } from '../../../hooks/useUsers'
import { compareDates } from '../../../helpers/dateHelper'

export type UserBaseTierListProps<ParentT extends RateDto<TierT>, TierT extends BaseTierDto> = {
    labelPrefixKey: string
    defaultTierValue: () => TierT
    parentValues: ParentT
    createListItem: (index: number, item: TierT, editTier: (index: number) => void) => ReactElement
    createTierFormContainer: (props: TierDialogProps<ParentT, TierT>) => React.ReactElement
    validationSchema: (translate: (key: string) => string) => Yup.ObjectSchema<any>
}

export enum TierAction {
    ADD = 'add',
    EDIT = 'edit',
}

export function sortTiers<TierT extends BaseTierDto>(users: UsersProps, tiers: TierT[]): TierT[] {
    tiers?.sort(
        (a, b) =>
            users.getUserById(a['userId'])?.fullName?.localeCompare(users.getUserById(b['userId'])?.fullName) ||
            compareDates(b.validFrom, b.validTo, a.validFrom, a.validTo),
    )
    return tiers
}

export default function TierList<ParentT extends RateDto<TierT>, TierT extends BaseTierDto>(
    props: UserBaseTierListProps<ParentT, TierT>,
): ReactElement {
    const { t: translate } = useTranslation()
    const { values, setFieldValue } = useFormikContext<ParentT>()
    const users = useUsers()

    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [index, setIndex] = useState(null)
    const [action, setAction] = useState<TierAction>()

    const [currentTier, setCurrentTier] = useState<TierT>(props.defaultTierValue())

    const addNewTier = () => {
        setCurrentTier(props.defaultTierValue())
        setAction(TierAction.ADD)
        setIndex(values.tiers?.length)
        setOpenDialog(true)
    }
    const editTier = (index: number) => {
        setCurrentTier({ ...props.defaultTierValue(), ...values.tiers?.[index] })
        setAction(TierAction.EDIT)
        setIndex(index)
        setOpenDialog(true)
    }
    const onDismiss = () => {
        setOpenDialog(false)
    }
    const onSubmit = (index: number, tierValues: TierT) => {
        const tiers = values.tiers ? values.tiers : [tierValues]
        tiers[index] = tierValues
        sortTiers(users, tiers)
        setFieldValue('tiers', [...tiers])
        setOpenDialog(false)
    }
    const onDelete = (index: number) => {
        const tiers = values.tiers?.filter((_tier, tierIndex) => tierIndex !== index)
        sortTiers(users, tiers)
        setFieldValue('tiers', [...tiers])
        setOpenDialog(false)
    }

    return (
        <>
            <Button style={{ marginTop: 10 }} appearance="primary" icon={<GuestAdd24Regular />} onClick={addNewTier}>
                {translate(`${props.labelPrefixKey}.add`)}
            </Button>
            {values.tiers?.length > 0 && (
                <BaseList<TierT> items={values.tiers}>{(item: TierT, index: number) => props.createListItem(index, item, editTier)}</BaseList>
            )}
            <TierDialog<ParentT, TierT>
                show={openDialog}
                setOpen={setOpenDialog}
                currentTier={currentTier}
                index={index}
                action={action}
                onDismiss={onDismiss}
                onSubmit={onSubmit}
                onDelete={onDelete}
                {...props}
            />
        </>
    )
}
